import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  MapPin,
  Phone,
  Quote,
  Target,
  TrendingUp,
  Wrench,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import {
  CASE_STUDIES,
  CASE_STUDY_SLUGS,
  getCaseStudy,
  type CaseStudy,
} from "@/lib/caseStudiesData";
import {
  SCHEMA_ORG_ID,
  SCHEMA_PERSON_ID,
  SITE_URL,
} from "@/lib/entitySchema";

function buildArticleLd(c: CaseStudy) {
  const canonical = `${SITE_URL}/case-studies/${c.slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `${canonical}#article`,
        headline: c.headline,
        description: c.summary,
        datePublished: c.datePublished,
        dateModified: c.datePublished,
        image: `${SITE_URL}/logo.png`,
        inLanguage: "en-US",
        author: { "@id": SCHEMA_PERSON_ID },
        publisher: { "@id": SCHEMA_ORG_ID },
        mainEntityOfPage: { "@id": canonical },
        about: [
          { "@type": "Thing", name: c.industry },
          { "@type": "Place", name: c.location },
        ],
        keywords: [c.industry, c.location, ...c.service].join(", "),
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          {
            "@type": "ListItem",
            position: 2,
            name: "Case Studies",
            item: `${SITE_URL}/case-studies`,
          },
          { "@type": "ListItem", position: 3, name: c.headline, item: canonical },
        ],
      },
    ],
  };
}

export const Route = createFileRoute("/case-studies/$studySlug")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  loader: ({ params }) => {
    const study = getCaseStudy(params.studySlug);
    if (!study) throw notFound();
    return { study };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const c = loaderData.study;
    const canonical = `${SITE_URL}/case-studies/${c.slug}`;
    return {
      meta: [
        { title: `${c.headline} — Case Study | Colorado Web Impressions` },
        { name: "description", content: c.summary },
        { property: "og:title", content: c.headline },
        { property: "og:description", content: c.summary },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildArticleLd(c)),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <SiteLayout>
      <section className="bg-background py-24 text-center">
        <div className="mx-auto max-w-xl px-5">
          <h1 className="text-3xl font-extrabold">Case study not found</h1>
          <p className="mt-3 text-muted-foreground">
            That case study doesn't exist or has been moved.
          </p>
          <Link
            to="/case-studies"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-gradient-gold px-5 py-3 font-bold text-brand-black shadow-gold"
          >
            <ArrowLeft className="h-4 w-4" /> Back to all case studies
          </Link>
        </div>
      </section>
    </SiteLayout>
  ),
  errorComponent: () => (
    <SiteLayout>
      <section className="bg-background py-24 text-center">
        <div className="mx-auto max-w-xl px-5">
          <h1 className="text-3xl font-extrabold">Something went wrong</h1>
          <p className="mt-3 text-muted-foreground">We couldn't load this case study — please try again.</p>
        </div>
      </section>
    </SiteLayout>
  ),
  component: CaseStudyPage,
});

function CaseStudyPage() {
  const { study: c } = Route.useLoaderData();
  const others = CASE_STUDIES.filter((x) => x.slug !== c.slug).slice(0, 3);

  return (
    <SiteLayout>
      <PageHero
        eyebrow={`Case Study · ${c.industry}`}
        title={
          <>
            {c.headline.split(" ").slice(0, -2).join(" ")}{" "}
            <span className="text-brand-gold">
              {c.headline.split(" ").slice(-2).join(" ")}
            </span>
          </>
        }
        description={c.summary}
      />

      <section className="border-b border-border bg-section-alt">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-2 px-5 py-4 text-xs lg:px-8">
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 font-semibold text-foreground/80 transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
          >
            <ArrowLeft className="h-3 w-3" /> All case studies
          </Link>
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1.5 font-semibold text-foreground/80">
            <MapPin className="h-3 w-3 text-brand-gold-deep" />
            {c.location}
          </span>
          <span className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1.5 font-semibold text-foreground/80">
            {c.industry}
          </span>
          <span className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1.5 font-semibold text-foreground/80">
            {c.service.join(" · ")}
          </span>
        </div>
      </section>

      <article className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card sm:p-8">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-brand-gold/10 text-brand-gold-deep">
                <Target className="h-5 w-5" />
              </div>
              <h2 className="mt-4 text-xl font-extrabold text-foreground">
                The problem
              </h2>
              <p className="mt-3 text-base leading-relaxed text-foreground/85">
                {c.problem}
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6 shadow-card sm:p-8">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-brand-gold/10 text-brand-gold-deep">
                <Wrench className="h-5 w-5" />
              </div>
              <h2 className="mt-4 text-xl font-extrabold text-foreground">
                What we did
              </h2>
              <ul className="mt-3 space-y-2.5">
                {c.approach.map((step: string) => (
                  <li
                    key={step}
                    className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground/85"
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-14">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-brand-gold-deep" />
              <h2 className="text-2xl font-extrabold text-foreground">
                The outcome
              </h2>
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {c.results.map((r: { label: string; value: string; context?: string }) => (
                <div
                  key={r.label}
                  className="rounded-2xl border border-border bg-gradient-to-br from-brand-gold/10 via-card to-card p-5 shadow-card"
                >
                  <div className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                    {r.label}
                  </div>
                  <div className="mt-2 text-2xl font-extrabold text-foreground">
                    {r.value}
                  </div>
                  {r.context && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {r.context}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {c.quote && (
            <figure className="mt-14 rounded-2xl border-l-4 border-brand-gold bg-muted/40 p-6 sm:p-8">
              <Quote className="h-8 w-8 text-brand-gold-deep" />
              <blockquote className="mt-3 text-lg italic leading-relaxed text-foreground/90">
                "{c.quote.text}"
              </blockquote>
              <figcaption className="mt-4 text-sm font-bold text-brand-gold-deep">
                — {c.quote.attribution}
              </figcaption>
            </figure>
          )}

          {others.length > 0 && (
            <div className="mt-20">
              <h2 className="text-2xl font-extrabold text-foreground">
                More Colorado case studies
              </h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {others.map((o) => (
                  <Link
                    key={o.slug}
                    to="/case-studies/$studySlug"
                    params={{ studySlug: o.slug }}
                    className="group rounded-2xl border border-border bg-card p-5 shadow-card transition-smooth hover:border-brand-gold/60 hover:shadow-card-hover"
                  >
                    <div className="text-[11px] font-bold uppercase tracking-wider text-brand-gold-deep">
                      {o.industry}
                    </div>
                    <div className="mt-2 text-base font-extrabold leading-snug text-foreground group-hover:text-brand-gold-deep">
                      {o.headline}
                    </div>
                    <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-brand-gold-deep">
                      Read case study <ArrowRight className="h-3 w-3" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      <section className="bg-section-alt py-20">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Want similar results for your Colorado business?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Talk directly with Chris — owner-led, no middlemen, no outsourcing.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Get a Free Proposal
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 rounded-md border-2 border-foreground/20 bg-card px-6 py-3 text-base font-bold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

export { CASE_STUDY_SLUGS };
